/**
 * Cycle de vie des demandes de matériel — domaine partagé backend/frontend.
 *
 * Une demande parcourt quatre étapes de validation :
 *
 *   [création] ─► AVALIDER ──manager(✓)──► ACCEPTEE ──service(✓)──► APPROUVEE ──complete()──► TERMINEE
 *                    │ manager(✗)              │ service(✗)
 *                REFUSEE ⊣                  REJETEE ⊣           (états terminaux)
 *
 * Ce module reste **pur** (ni DOM, ni Prisma) : le backend rejoue ses parseurs sur le corps
 * de requête, le frontend s'appuie sur ses libellés et sa machine à états pour l'affichage.
 * Le prototype ne gardait **aucune** autorisation : elles sont toutes vérifiées côté serveur.
 */

import { CATEGORY_OF_TYPE, SERVICE_BY_CATEGORY, isTypeKey, type CategoryKey, type ServiceKey, type SituationKey, type TypeKey } from './catalog.js';
import type { ParseResult } from './validation.js';

// ---------------------------------------------------------------------------
// Énumérations
// ---------------------------------------------------------------------------

export const REQUEST_INTENT_KEYS = ['NEW', 'REPLACE'] as const;
export type RequestIntent = (typeof REQUEST_INTENT_KEYS)[number];

export const REQUEST_STATUS_KEYS = [
  'AVALIDER',
  'ACCEPTEE',
  'REFUSEE',
  'APPROUVEE',
  'REJETEE',
  'TERMINEE',
] as const;
export type RequestStatus = (typeof REQUEST_STATUS_KEYS)[number];

/** Lieu de livraison demandé. Sous-ensemble de `LocationKind`, sans le stockage. */
export const DELIVERY_KIND_KEYS = ['SIEGE', 'SITE', 'MULTI', 'HOME'] as const;
export type DeliveryKind = (typeof DELIVERY_KIND_KEYS)[number];

export const REQUEST_EVENT_TYPE_KEYS = [
  'CREATED',
  'EDITED',
  'MANAGER_ACCEPTED',
  'MANAGER_REFUSED',
  'SERVICE_APPROVED',
  'SERVICE_REJECTED',
  'COMPLETED',
] as const;
export type RequestEventType = (typeof REQUEST_EVENT_TYPE_KEYS)[number];

export function isRequestIntent(value: unknown): value is RequestIntent {
  return typeof value === 'string' && (REQUEST_INTENT_KEYS as readonly string[]).includes(value);
}

export function isDeliveryKind(value: unknown): value is DeliveryKind {
  return typeof value === 'string' && (DELIVERY_KIND_KEYS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Métadonnées d'affichage (verbatim prototype)
// ---------------------------------------------------------------------------

/** Ton visuel d'un statut, résolu en couleur de badge côté front (tokens uniquement). */
export type StatusTone = 'warning' | 'info' | 'brand' | 'error' | 'success';

export interface RequestStatusMeta {
  label: string;
  tone: StatusTone;
  /** Rang dans la progression (1→4). `-1` pour les états terminaux d'échec. */
  step: number;
}

export const REQUEST_STATUS_META: Record<RequestStatus, RequestStatusMeta> = {
  AVALIDER: { label: 'À valider', tone: 'warning', step: 1 },
  ACCEPTEE: { label: 'Acceptée', tone: 'info', step: 2 },
  REFUSEE: { label: 'Refusée', tone: 'error', step: -1 },
  APPROUVEE: { label: 'Approuvée', tone: 'brand', step: 3 },
  REJETEE: { label: 'Rejetée', tone: 'error', step: -1 },
  TERMINEE: { label: 'Terminée', tone: 'success', step: 4 },
};

export const REQUEST_INTENT_LABELS: Record<RequestIntent, string> = {
  NEW: 'Nouveau matériel',
  REPLACE: 'Remplacement',
};

/** Libellé court pour la colonne « Type » du datagrid. */
export const REQUEST_INTENT_SHORT: Record<RequestIntent, string> = {
  NEW: 'Nouveau',
  REPLACE: 'Remplacement',
};

export const DELIVERY_KIND_LABELS: Record<DeliveryKind, string> = {
  SIEGE: 'Au siège',
  SITE: 'Sur un site',
  MULTI: 'Multisites',
  HOME: 'Home Office',
};

/** Les quatre jalons de la barre de progression. `{service}` est substitué à l'affichage. */
export const REQUEST_STEP_LABELS = [
  'Validation manager',
  'Approbation {service}',
  'Finalisation inventaire',
  'Terminée',
] as const;

/**
 * Libellé français d'un événement du journal. `SERVICE_APPROVED`/`SERVICE_REJECTED`
 * mentionnent le service responsable (« Approuvée par le QPL »).
 */
export function requestEventLabel(type: RequestEventType, service?: ServiceKey): string {
  switch (type) {
    case 'CREATED':
      return 'Demande créée';
    case 'EDITED':
      return 'Demande modifiée';
    case 'MANAGER_ACCEPTED':
      return 'Acceptée par le manager';
    case 'MANAGER_REFUSED':
      return 'Refusée par le manager';
    case 'SERVICE_APPROVED':
      return `Approuvée par le ${service ?? 'service'}`;
    case 'SERVICE_REJECTED':
      return `Rejetée par le ${service ?? 'service'}`;
    case 'COMPLETED':
      return 'Inventaire renseigné — terminée';
  }
}

// ---------------------------------------------------------------------------
// Machine à états — pure, rejouée côté serveur avant toute écriture
// ---------------------------------------------------------------------------

/** États terminaux : plus aucune transition possible. */
export function isTerminalStatus(status: RequestStatus): boolean {
  return status === 'REFUSEE' || status === 'REJETEE' || status === 'TERMINEE';
}

/**
 * Une demande « à traiter » attend une action de quelqu'un dans le cycle — c'est ce qui
 * alimente le badge coral de la barre latérale. Ni terminée, ni refusée, ni rejetée.
 */
export function isPendingStatus(status: RequestStatus): boolean {
  return status === 'AVALIDER' || status === 'ACCEPTEE' || status === 'APPROUVEE';
}

/** La décision du manager n'a lieu que sur une demande à valider. */
export function canManagerDecide(status: RequestStatus): boolean {
  return status === 'AVALIDER';
}

/** L'approbation du service n'a lieu que sur une demande acceptée par le manager. */
export function canServiceDecide(status: RequestStatus): boolean {
  return status === 'ACCEPTEE';
}

/** La finalisation (création de l'équipement) n'a lieu que sur une demande approuvée. */
export function canComplete(status: RequestStatus): boolean {
  return status === 'APPROUVEE';
}

/**
 * Édition du contenu de la demande. Le demandeur ne peut corriger que tant qu'elle est
 * `AVALIDER` ; un manager ou un administrateur peut encore l'ajuster une fois `ACCEPTEE`.
 */
export function canEditRequest(status: RequestStatus, privileged: boolean): boolean {
  if (status === 'AVALIDER') return true;
  return privileged && status === 'ACCEPTEE';
}

/** Statut résultant d'une décision de manager. */
export function statusAfterManager(approve: boolean): RequestStatus {
  return approve ? 'ACCEPTEE' : 'REFUSEE';
}

/** Statut résultant d'une décision de service. */
export function statusAfterService(approve: boolean): RequestStatus {
  return approve ? 'APPROUVEE' : 'REJETEE';
}

/** Service responsable de l'approbation d'une catégorie (NET → QPL depuis le lot 5). */
export function approvingService(category: CategoryKey): ServiceKey {
  return SERVICE_BY_CATEGORY[category];
}

/** Situation de déclaration correspondant à un lieu de livraison (pour la finalisation). */
export function situationForDelivery(kind: DeliveryKind): SituationKey {
  return kind === 'SITE' ? 'FIXE' : kind;
}

/** Lieu de livraison correspondant à une situation (au moment de finaliser un remplacement). */
export function deliveryForSituation(situation: SituationKey): DeliveryKind {
  return situation === 'FIXE' ? 'SITE' : situation;
}

// ---------------------------------------------------------------------------
// Référence — CAV-AAAA-NNNN, générée côté serveur
// ---------------------------------------------------------------------------

export const REQUEST_REFERENCE_PATTERN = /^CAV-[0-9]{4}-[0-9]{4,}$/;

export function formatRequestReference(year: number, sequence: number): string {
  return `CAV-${year}-${String(sequence).padStart(4, '0')}`;
}

// ---------------------------------------------------------------------------
// Analyse des corps de requête — le serveur ne fait jamais confiance au client
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

function optionalId(raw: unknown): number | null {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function optionalText(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : '';
}

/** Corps validé d'une création de demande. Le type/catégorie d'un remplacement est résolu côté serveur. */
export type CreateRequestInput =
  | {
      intent: 'NEW';
      typeKey: TypeKey;
      categoryKey: CategoryKey;
      deliveryKind: DeliveryKind;
      becaneSiteId: number | null;
      motif: string;
      ownerId: number | null;
    }
  | {
      intent: 'REPLACE';
      replaceEquipmentId: number;
      motif: string;
      ownerId: number | null;
    };

export function parseCreateRequestInput(raw: unknown): ParseResult<CreateRequestInput> {
  const body = asRecord(raw);
  if (!body) return { ok: false, error: 'Demande invalide.' };

  const motif = optionalText(body.motif);
  const ownerId = optionalId(body.ownerId);

  if (!isRequestIntent(body.intent)) {
    return { ok: false, error: 'Intention de demande manquante ou inconnue.' };
  }

  if (body.intent === 'REPLACE') {
    const replaceEquipmentId = optionalId(body.replaceEquipmentId);
    if (!replaceEquipmentId) {
      return { ok: false, error: 'Un matériel à remplacer doit être choisi.' };
    }
    return { ok: true, value: { intent: 'REPLACE', replaceEquipmentId, motif, ownerId } };
  }

  // intent === 'NEW'
  const typeKey = body.typeKey;
  if (typeof typeKey !== 'string' || !isTypeKey(typeKey)) {
    return { ok: false, error: `Type de matériel inconnu : ${String(typeKey)}` };
  }
  if (!isDeliveryKind(body.deliveryKind)) {
    return { ok: false, error: 'Un lieu de livraison doit être choisi.' };
  }

  let becaneSiteId: number | null = null;
  if (body.deliveryKind === 'SITE') {
    becaneSiteId = optionalId(body.becaneSiteId);
    if (!becaneSiteId) {
      return { ok: false, error: 'Un site doit être choisi pour une livraison sur site.' };
    }
  }

  return {
    ok: true,
    value: {
      intent: 'NEW',
      typeKey,
      categoryKey: CATEGORY_OF_TYPE[typeKey],
      deliveryKind: body.deliveryKind,
      becaneSiteId,
      motif,
      ownerId,
    },
  };
}

export interface RequestDecisionInput {
  approve: boolean;
  comment: string;
}

export function parseRequestDecisionInput(raw: unknown): ParseResult<RequestDecisionInput> {
  const body = asRecord(raw);
  if (!body) return { ok: false, error: 'Décision invalide.' };
  if (typeof body.approve !== 'boolean') {
    return { ok: false, error: 'Décision manquante (accepter ou refuser).' };
  }
  return { ok: true, value: { approve: body.approve, comment: optionalText(body.comment) } };
}
